/**
 * 8-ball pool: table geometry, a deterministic 2D physics step, and shot
 * resolution. No DOM — the API and the web client have to agree on collisions
 * and on who won.
 *
 * The legacy engine in `8ball/physics/engine.js` used a half-finished "mouth
 * lock" plus extra jaw segments; balls tunneled, overlapped, bounced out of
 * pockets, and got stuck in rails. This file replaces that model with equal-mass
 * balls, conservation of momentum, cushions whose gaps match the pocket
 * openings, and a capture radius that swallows a ball that hits the hole.
 * Misses bounce; they do not leave the felt.
 */

export const POOL = {
  TABLE_W: 800,
  TABLE_H: 400,
  RAIL: 28,
  BALL_R: 10,
  /** 60 Hz game tick, matching the old server loop. */
  TICK_HZ: 60,
  TICK_DT: 1 / 60,
  /** Physics substeps per tick so a fast cue cannot tunnel through a ball. */
  SUBSTEPS: 8,
  MAX_STEPS: 60 * 20,
  MAX_SPEED: 1100,
  STOP_SPEED: 8,
  /** Exponential drag per second; ~0.985 per 1/60 s in the old engine. */
  DRAG: 0.9,
  BALL_E: 0.96,
  CUSHION_E: 0.74,
  TANGENT_DAMP: 0.82,
  STRIKE_SPEED: 760,
  MIN_POWER: 5,
  MAX_POWER: 100,
  CORNER_POCKET_R: 26,
  SIDE_POCKET_R: 22,
  /** Center-in-hole radius; must match the drawn pocket and the rail gap. */
  CORNER_CAPTURE: 26,
  SIDE_CAPTURE: 22,
  CORNER_MOUTH: 32,
  SIDE_MOUTH: 28,
  /** Rail opening = pocket radius, so a miss hits cushion instead of the void. */
  CORNER_GAP: 26,
  SIDE_GAP: 22,
  POCKET_PULL: 320,
  SETTLE_ITERS: 8,
  PAIR_ITERS: 6,
} as const;

export const POOL_TICK_DT = POOL.TICK_DT;

export type SuitGroup = "solids" | "stripes";
export type EightballSeat = 0 | 1;

export interface PoolBall {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  pocketed: boolean;
}

export interface PoolWorld {
  balls: PoolBall[];
  firstHitId: number | null;
}

export interface PoolVec {
  x: number;
  y: number;
}

export interface PoolPocket {
  id: "TL" | "TR" | "BL" | "BR" | "TM" | "BM";
  x: number;
  y: number;
  r: number;
  capture: number;
  mouth: number;
}

export interface PoolSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  nx: number;
  ny: number;
}

export interface PoolPlay {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
  midX: number;
  midY: number;
}

const LEFT = POOL.RAIL;
const TOP = POOL.RAIL;
const RIGHT = POOL.TABLE_W - POOL.RAIL;
const BOTTOM = POOL.TABLE_H - POOL.RAIL;
const MID_X = (LEFT + RIGHT) * 0.5;
const MID_Y = (TOP + BOTTOM) * 0.5;

export const POOL_PLAY: PoolPlay = {
  left: LEFT,
  top: TOP,
  right: RIGHT,
  bottom: BOTTOM,
  width: RIGHT - LEFT,
  height: BOTTOM - TOP,
  midX: MID_X,
  midY: MID_Y,
};

export const POOL_HEAD_SPOT: PoolVec = {
  x: LEFT + (RIGHT - LEFT) * 0.25,
  y: MID_Y,
};

export const POOL_FOOT_SPOT: PoolVec = {
  x: LEFT + (RIGHT - LEFT) * 0.75,
  y: MID_Y,
};

export const POOL_POCKETS: readonly PoolPocket[] = [
  { id: "TL", x: LEFT, y: TOP, r: POOL.CORNER_POCKET_R, capture: POOL.CORNER_CAPTURE, mouth: POOL.CORNER_MOUTH },
  { id: "TR", x: RIGHT, y: TOP, r: POOL.CORNER_POCKET_R, capture: POOL.CORNER_CAPTURE, mouth: POOL.CORNER_MOUTH },
  { id: "BL", x: LEFT, y: BOTTOM, r: POOL.CORNER_POCKET_R, capture: POOL.CORNER_CAPTURE, mouth: POOL.CORNER_MOUTH },
  { id: "BR", x: RIGHT, y: BOTTOM, r: POOL.CORNER_POCKET_R, capture: POOL.CORNER_CAPTURE, mouth: POOL.CORNER_MOUTH },
  { id: "TM", x: MID_X, y: TOP, r: POOL.SIDE_POCKET_R, capture: POOL.SIDE_CAPTURE, mouth: POOL.SIDE_MOUTH },
  { id: "BM", x: MID_X, y: BOTTOM, r: POOL.SIDE_POCKET_R, capture: POOL.SIDE_CAPTURE, mouth: POOL.SIDE_MOUTH },
];

function makeSeg(x1: number, y1: number, x2: number, y2: number): PoolSegment {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  let nx = -dy / len;
  let ny = dx / len;
  const mx = (x1 + x2) * 0.5;
  const my = (y1 + y2) * 0.5;
  if ((MID_X - mx) * nx + (MID_Y - my) * ny < 0) {
    nx = -nx;
    ny = -ny;
  }
  return { x1, y1, x2, y2, nx, ny };
}

export const POOL_CUSHIONS: readonly PoolSegment[] = [
  makeSeg(LEFT + POOL.CORNER_GAP, TOP, MID_X - POOL.SIDE_GAP, TOP),
  makeSeg(MID_X + POOL.SIDE_GAP, TOP, RIGHT - POOL.CORNER_GAP, TOP),
  makeSeg(LEFT + POOL.CORNER_GAP, BOTTOM, MID_X - POOL.SIDE_GAP, BOTTOM),
  makeSeg(MID_X + POOL.SIDE_GAP, BOTTOM, RIGHT - POOL.CORNER_GAP, BOTTOM),
  makeSeg(LEFT, TOP + POOL.CORNER_GAP, LEFT, BOTTOM - POOL.CORNER_GAP),
  makeSeg(RIGHT, TOP + POOL.CORNER_GAP, RIGHT, BOTTOM - POOL.CORNER_GAP),
];

export const EIGHTBALL_SOLID_COLORS: Readonly<Record<number, string>> = {
  1: "#f4c20d",
  2: "#1a73e8",
  3: "#d93025",
  4: "#7b1fa2",
  5: "#f57c00",
  6: "#0b8043",
  7: "#8b0000",
};

export const EIGHTBALL_STRIPE_COLORS: Readonly<Record<number, string>> = {
  9: "#f4c20d",
  10: "#1a73e8",
  11: "#d93025",
  12: "#7b1fa2",
  13: "#f57c00",
  14: "#0b8043",
  15: "#8b0000",
};

export const EIGHTBALL_GROUP_LABEL: Record<SuitGroup, string> = {
  solids: "całe",
  stripes: "połówki",
};

export const EIGHTBALL_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

export type EightballFoul = "scratch" | "no_hit" | "wrong_first";

export const EIGHTBALL_FOUL_LABEL: Record<EightballFoul, string> = {
  scratch: "Faul: wpadła biała. Przeciwnik ma bilę w ręce.",
  no_hit: "Faul: żadna bila nie została trafiona.",
  wrong_first: "Faul: zła pierwsza bila.",
};

export interface EightballSeatView {
  userId: number | null;
  name: string;
  connected: boolean;
}

export interface EightballSnapshot {
  room: string;
  balls: PoolBall[];
  simRunning: boolean;
  currentTurn: EightballSeat;
  ballInHand: boolean;
  openTable: boolean;
  groups: [SuitGroup | null, SuitGroup | null];
  players: [EightballSeatView, EightballSeatView];
  pocketedIds: number[];
  gameEnded: boolean;
  winner: EightballSeat | null;
  waitingRematch: [boolean, boolean];
  spectatorCount: number;
  message: string | null;
}

export const EMPTY_EIGHTBALL_SEAT: EightballSeatView = {
  userId: null,
  name: "Wolne",
  connected: false,
};

export const EMPTY_EIGHTBALL_SNAPSHOT: EightballSnapshot = {
  room: "",
  balls: [],
  simRunning: false,
  currentTurn: 0,
  ballInHand: false,
  openTable: true,
  groups: [null, null],
  players: [EMPTY_EIGHTBALL_SEAT, EMPTY_EIGHTBALL_SEAT],
  pocketedIds: [],
  gameEnded: false,
  winner: null,
  waitingRematch: [false, false],
  spectatorCount: 0,
  message: null,
};

export interface EightballJoinRequest {
  room?: string;
}

export interface EightballStrikeRequest {
  angleDeg: number;
  power: number;
}

export interface EightballPlaceRequest {
  x: number;
  y: number;
}

export interface EightballSimResult {
  pocketed: number[];
  steps: number;
  stopped: boolean;
}

export interface EightballShotInput {
  shooter: EightballSeat;
  openTable: boolean;
  groups: [SuitGroup | null, SuitGroup | null];
  pocketedIds: readonly number[];
  firstHitId: number | null;
  balls: readonly PoolBall[];
}

export interface EightballShotResult {
  foul: EightballFoul | null;
  openTable: boolean;
  groups: [SuitGroup | null, SuitGroup | null];
  keepTurn: boolean;
  ballInHand: boolean;
  gameEnded: boolean;
  winner: EightballSeat | null;
  message: string;
}

export function otherSeat(seat: EightballSeat): EightballSeat {
  return seat === 0 ? 1 : 0;
}

export function ballGroup(id: number): SuitGroup | "eight" | null {
  if (id >= 1 && id <= 7) return "solids";
  if (id >= 9 && id <= 15) return "stripes";
  if (id === 8) return "eight";
  return null;
}

export function suitOf(id: number): SuitGroup | null {
  const group = ballGroup(id);
  return group === "solids" || group === "stripes" ? group : null;
}

export function makePoolBall(
  id: number,
  x: number,
  y: number,
  vx = 0,
  vy = 0,
  pocketed = false,
): PoolBall {
  return { id, x, y, vx, vy, pocketed };
}

export function clonePoolBall(ball: PoolBall): PoolBall {
  return {
    id: ball.id,
    x: ball.x,
    y: ball.y,
    vx: ball.vx,
    vy: ball.vy,
    pocketed: ball.pocketed,
  };
}

export function clonePoolWorld(world: PoolWorld): PoolWorld {
  return {
    balls: world.balls.map(clonePoolBall),
    firstHitId: world.firstHitId,
  };
}

export function findPoolBall(balls: readonly PoolBall[], id: number): PoolBall | undefined {
  return balls.find((ball) => ball.id === id);
}

export function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

export function shuffleInPlace<T>(items: T[], rng: () => number): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const a = items[i];
    const b = items[j];
    if (a === undefined || b === undefined) continue;
    items[i] = b;
    items[j] = a;
  }
  return items;
}

export function randomRoomCode(rng: () => number = Math.random, length = 5): string {
  let out = "";
  for (let i = 0; i < length; i++) {
    const idx = Math.min(
      EIGHTBALL_CODE_ALPHABET.length - 1,
      Math.max(0, Math.floor(rng() * EIGHTBALL_CODE_ALPHABET.length)),
    );
    out += EIGHTBALL_CODE_ALPHABET[idx] ?? "A";
  }
  return out;
}

export function normalizeRoomCode(raw: string): string | null {
  const code = raw.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
  return code.length >= 3 ? code : null;
}

export function clampPower(power: number): number {
  if (!Number.isFinite(power)) return 50;
  return Math.min(POOL.MAX_POWER, Math.max(POOL.MIN_POWER, power));
}

export function clampAngleDeg(angleDeg: number): number {
  if (!Number.isFinite(angleDeg)) return 0;
  let a = angleDeg % 360;
  if (a > 180) a -= 360;
  if (a <= -180) a += 360;
  return a;
}

function capSpeed(ball: PoolBall): void {
  const speed = Math.hypot(ball.vx, ball.vy);
  if (speed <= POOL.MAX_SPEED || speed === 0) return;
  const s = POOL.MAX_SPEED / speed;
  ball.vx *= s;
  ball.vy *= s;
}

function stopIfSlow(ball: PoolBall): void {
  if (Math.hypot(ball.vx, ball.vy) < POOL.STOP_SPEED) {
    ball.vx = 0;
    ball.vy = 0;
  }
}

export function allBallsStopped(world: PoolWorld): boolean {
  for (const ball of world.balls) {
    if (ball.pocketed) continue;
    if (Math.hypot(ball.vx, ball.vy) >= POOL.STOP_SPEED) return false;
  }
  return true;
}

export function hasPoolNaN(world: PoolWorld): boolean {
  for (const ball of world.balls) {
    if (
      !Number.isFinite(ball.x) ||
      !Number.isFinite(ball.y) ||
      !Number.isFinite(ball.vx) ||
      !Number.isFinite(ball.vy)
    ) {
      return true;
    }
  }
  return false;
}

export function repairPoolNaN(world: PoolWorld): void {
  for (const ball of world.balls) {
    if (!Number.isFinite(ball.x)) ball.x = POOL_HEAD_SPOT.x;
    if (!Number.isFinite(ball.y)) ball.y = POOL_HEAD_SPOT.y;
    if (!Number.isFinite(ball.vx)) ball.vx = 0;
    if (!Number.isFinite(ball.vy)) ball.vy = 0;
  }
}

function mouthOf(x: number, y: number): PoolPocket | null {
  let found: PoolPocket | null = null;
  let best = Infinity;
  for (const pocket of POOL_POCKETS) {
    const d = Math.hypot(pocket.x - x, pocket.y - y);
    if (d <= pocket.mouth && d < best) {
      found = pocket;
      best = d;
    }
  }
  return found;
}

function segmentHitsCircle(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  cx: number,
  cy: number,
  radius: number,
): boolean {
  if (Math.hypot(x0 - cx, y0 - cy) <= radius) return true;
  if (Math.hypot(x1 - cx, y1 - cy) <= radius) return true;
  const dx = x1 - x0;
  const dy = y1 - y0;
  const fx = x0 - cx;
  const fy = y0 - cy;
  const a = dx * dx + dy * dy;
  if (a < 1e-12) return Math.hypot(fx, fy) <= radius;
  const b = 2 * (fx * dx + fy * dy);
  const c = fx * fx + fy * fy - radius * radius;
  const disc = b * b - 4 * a * c;
  if (disc < 0) return false;
  const sqrt = Math.sqrt(disc);
  const t1 = (-b - sqrt) / (2 * a);
  const t2 = (-b + sqrt) / (2 * a);
  return (t1 >= 0 && t1 <= 1) || (t2 >= 0 && t2 <= 1);
}

function potBall(ball: PoolBall, pocket: PoolPocket): void {
  ball.pocketed = true;
  ball.vx = 0;
  ball.vy = 0;
  ball.x = pocket.x;
  ball.y = pocket.y;
}

function captureBalls(world: PoolWorld, prevX: number[], prevY: number[]): number[] {
  const newly: number[] = [];
  for (let i = 0; i < world.balls.length; i++) {
    const ball = world.balls[i];
    if (!ball || ball.pocketed) continue;
    const ox = prevX[i] ?? ball.x;
    const oy = prevY[i] ?? ball.y;
    for (const pocket of POOL_POCKETS) {
      const d = Math.hypot(pocket.x - ball.x, pocket.y - ball.y);
      if (
        d <= pocket.capture ||
        segmentHitsCircle(ox, oy, ball.x, ball.y, pocket.x, pocket.y, pocket.capture)
      ) {
        potBall(ball, pocket);
        newly.push(ball.id);
        break;
      }
    }
  }
  return newly;
}

function bounceAxis(ball: PoolBall, nx: number, ny: number): void {
  const vn = ball.vx * nx + ball.vy * ny;
  if (vn >= 0) return;
  const vnx = vn * nx;
  const vny = vn * ny;
  const vtx = ball.vx - vnx;
  const vty = ball.vy - vny;
  ball.vx = vtx * POOL.TANGENT_DAMP - vnx * POOL.CUSHION_E;
  ball.vy = vty * POOL.TANGENT_DAMP - vny * POOL.CUSHION_E;
}

/** Misses bounce; they must not leave the felt through a rail gap. */
function containBalls(world: PoolWorld): void {
  const r = POOL.BALL_R;
  const minX = LEFT + r;
  const maxX = RIGHT - r;
  const minY = TOP + r;
  const maxY = BOTTOM - r;
  for (const ball of world.balls) {
    if (ball.pocketed) continue;
    if (ball.x < minX) {
      ball.x = minX;
      bounceAxis(ball, 1, 0);
    } else if (ball.x > maxX) {
      ball.x = maxX;
      bounceAxis(ball, -1, 0);
    }
    if (ball.y < minY) {
      ball.y = minY;
      bounceAxis(ball, 0, 1);
    } else if (ball.y > maxY) {
      ball.y = maxY;
      bounceAxis(ball, 0, -1);
    }
  }
}

function applyPocketPull(ball: PoolBall, dt: number): void {
  const pocket = mouthOf(ball.x, ball.y);
  if (!pocket) return;
  const dx = pocket.x - ball.x;
  const dy = pocket.y - ball.y;
  const d = Math.hypot(dx, dy);
  if (d < 1e-6 || d > pocket.mouth) return;
  const heading = ball.vx * dx + ball.vy * dy;
  const speed = Math.hypot(ball.vx, ball.vy);
  if (heading <= 0 && speed > 140) return;
  const pull = POOL.POCKET_PULL * (1 - d / pocket.mouth);
  ball.vx += (dx / d) * pull * dt;
  ball.vy += (dy / d) * pull * dt;
}

function collideCushion(ball: PoolBall, seg: PoolSegment): void {
  const r = POOL.BALL_R;
  const vx = seg.x2 - seg.x1;
  const vy = seg.y2 - seg.y1;
  const len2 = vx * vx + vy * vy;
  if (len2 === 0) return;
  let t = ((ball.x - seg.x1) * vx + (ball.y - seg.y1) * vy) / len2;
  t = Math.max(0, Math.min(1, t));
  const px = seg.x1 + t * vx;
  const py = seg.y1 + t * vy;
  let dx = ball.x - px;
  let dy = ball.y - py;
  let dist = Math.hypot(dx, dy);
  if (dist >= r) return;
  let nx: number;
  let ny: number;
  if (dist < 1e-8) {
    nx = seg.nx;
    ny = seg.ny;
    dist = 0;
  } else {
    nx = dx / dist;
    ny = dy / dist;
    if (nx * seg.nx + ny * seg.ny < 0) {
      nx = seg.nx;
      ny = seg.ny;
    }
  }
  const pen = r - dist;
  ball.x += nx * pen;
  ball.y += ny * pen;
  const vn = ball.vx * nx + ball.vy * ny;
  if (vn < 0) {
    const vnx = vn * nx;
    const vny = vn * ny;
    const vtx = ball.vx - vnx;
    const vty = ball.vy - vny;
    ball.vx = vtx * POOL.TANGENT_DAMP - vnx * POOL.CUSHION_E;
    ball.vy = vty * POOL.TANGENT_DAMP - vny * POOL.CUSHION_E;
  }
}

function resolveCushions(world: PoolWorld): void {
  for (const ball of world.balls) {
    if (ball.pocketed) continue;
    for (const seg of POOL_CUSHIONS) {
      collideCushion(ball, seg);
    }
  }
}

function resolveBallPairs(world: PoolWorld, recordHit: boolean): void {
  const balls = world.balls;
  const minD = 2 * POOL.BALL_R;
  const minD2 = minD * minD;
  for (let iter = 0; iter < POOL.PAIR_ITERS; iter++) {
    for (let i = 0; i < balls.length; i++) {
      const a = balls[i];
      if (!a || a.pocketed) continue;
      for (let j = i + 1; j < balls.length; j++) {
        const b = balls[j];
        if (!b || b.pocketed) continue;
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let d2 = dx * dx + dy * dy;
        if (d2 >= minD2) continue;
        if (d2 < 1e-12) {
          dx = 0.01;
          dy = 0;
          d2 = dx * dx;
        }
        const d = Math.sqrt(d2);
        const nx = dx / d;
        const ny = dy / d;
        const overlap = minD - d;
        a.x -= nx * overlap * 0.5;
        a.y -= ny * overlap * 0.5;
        b.x += nx * overlap * 0.5;
        b.y += ny * overlap * 0.5;
        const rvx = b.vx - a.vx;
        const rvy = b.vy - a.vy;
        const relN = rvx * nx + rvy * ny;
        if (relN >= 0) continue;
        const impulse = (-(1 + POOL.BALL_E) * relN) / 2;
        a.vx -= impulse * nx;
        a.vy -= impulse * ny;
        b.vx += impulse * nx;
        b.vy += impulse * ny;
        capSpeed(a);
        capSpeed(b);
        if (recordHit && world.firstHitId === null) {
          if (a.id === 0 && b.id !== 0) world.firstHitId = b.id;
          else if (b.id === 0 && a.id !== 0) world.firstHitId = a.id;
        }
      }
    }
  }
}

function integrateBall(ball: PoolBall, dt: number): void {
  const speed = Math.hypot(ball.vx, ball.vy);
  if (speed < POOL.STOP_SPEED) {
    ball.vx = 0;
    ball.vy = 0;
    return;
  }
  const factor = Math.exp(-POOL.DRAG * dt);
  ball.vx *= factor;
  ball.vy *= factor;
  stopIfSlow(ball);
  ball.x += ball.vx * dt;
  ball.y += ball.vy * dt;
  applyPocketPull(ball, dt);
  capSpeed(ball);
}

export function stepPoolWorld(world: PoolWorld, dt: number): number[] {
  const sub = dt / POOL.SUBSTEPS;
  const newly: number[] = [];
  const prevX = world.balls.map((ball) => ball.x);
  const prevY = world.balls.map((ball) => ball.y);
  for (let s = 0; s < POOL.SUBSTEPS; s++) {
    for (let i = 0; i < world.balls.length; i++) {
      const ball = world.balls[i];
      if (!ball || ball.pocketed) continue;
      prevX[i] = ball.x;
      prevY[i] = ball.y;
      integrateBall(ball, sub);
    }
    newly.push(...captureBalls(world, prevX, prevY));
    resolveBallPairs(world, true);
    resolveCushions(world);
    resolveBallPairs(world, true);
    newly.push(...captureBalls(world, prevX, prevY));
    containBalls(world);
  }
  if (hasPoolNaN(world)) repairPoolNaN(world);
  for (const ball of world.balls) {
    if (!ball.pocketed) stopIfSlow(ball);
  }
  return newly;
}

export function settlePoolWorld(world: PoolWorld): void {
  for (let i = 0; i < POOL.SETTLE_ITERS; i++) {
    resolveBallPairs(world, false);
    resolveCushions(world);
    containBalls(world);
  }
  for (const ball of world.balls) {
    if (ball.pocketed) continue;
    ball.vx = 0;
    ball.vy = 0;
  }
}

export function simulateUntilRest(
  world: PoolWorld,
  maxSteps: number = POOL.MAX_STEPS,
): EightballSimResult {
  const pocketed: number[] = [];
  let steps = 0;
  while (steps < maxSteps && !allBallsStopped(world)) {
    const newly = stepPoolWorld(world, POOL.TICK_DT);
    for (const id of newly) pocketed.push(id);
    steps += 1;
    if (hasPoolNaN(world)) {
      repairPoolNaN(world);
      break;
    }
  }
  settlePoolWorld(world);
  return { pocketed, steps, stopped: allBallsStopped(world) };
}

export function ballsOverlap(world: PoolWorld, slop = 0.08): boolean {
  const minD = 2 * POOL.BALL_R - slop;
  const balls = world.balls;
  for (let i = 0; i < balls.length; i++) {
    const a = balls[i];
    if (!a || a.pocketed) continue;
    for (let j = i + 1; j < balls.length; j++) {
      const b = balls[j];
      if (!b || b.pocketed) continue;
      if (Math.hypot(b.x - a.x, b.y - a.y) < minD) return true;
    }
  }
  return false;
}

export function cueOverlapsOthers(world: PoolWorld, x: number, y: number): boolean {
  const minD = 2 * POOL.BALL_R + 0.4;
  for (const ball of world.balls) {
    if (ball.id === 0 || ball.pocketed) continue;
    if (Math.hypot(ball.x - x, ball.y - y) < minD) return true;
  }
  return false;
}

export function isLegalCuePosition(world: PoolWorld, x: number, y: number): boolean {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  const inset = POOL.BALL_R + 0.6;
  if (x < LEFT + inset || x > RIGHT - inset || y < TOP + inset || y > BOTTOM - inset) {
    return false;
  }
  for (const pocket of POOL_POCKETS) {
    if (Math.hypot(pocket.x - x, pocket.y - y) < pocket.capture + 2) return false;
  }
  return !cueOverlapsOthers(world, x, y);
}

export function placeCueBall(world: PoolWorld, x: number, y: number): boolean {
  if (!isLegalCuePosition(world, x, y)) return false;
  let cue = findPoolBall(world.balls, 0);
  if (!cue) {
    cue = makePoolBall(0, x, y);
    world.balls.push(cue);
  }
  cue.x = x;
  cue.y = y;
  cue.vx = 0;
  cue.vy = 0;
  cue.pocketed = false;
  return true;
}

export function strikeCue(world: PoolWorld, angleDeg: number, power: number): boolean {
  const cue = findPoolBall(world.balls, 0);
  if (!cue || cue.pocketed) return false;
  const power01 = clampPower(power) / 100;
  const speed = POOL.STRIKE_SPEED * power01;
  const a = (clampAngleDeg(angleDeg) * Math.PI) / 180;
  cue.vx = Math.cos(a) * speed;
  cue.vy = Math.sin(a) * speed;
  cue.pocketed = false;
  world.firstHitId = null;
  return true;
}

function trianglePositions(): PoolVec[] {
  const gap = 2 * POOL.BALL_R + 0.18;
  const rowGap = gap * Math.sin(Math.PI / 3);
  const positions: PoolVec[] = [];
  for (let row = 0; row < 5; row++) {
    const count = row + 1;
    const x = POOL_FOOT_SPOT.x + row * rowGap;
    const y0 = POOL_FOOT_SPOT.y - ((count - 1) * gap) / 2;
    for (let i = 0; i < count; i++) {
      positions.push({ x, y: y0 + i * gap });
    }
  }
  return positions;
}

export function createRack(rng: () => number = Math.random): PoolWorld {
  const positions = trianglePositions();
  const remaining = [2, 3, 4, 5, 6, 7, 9, 10, 11, 12, 13, 14, 15];
  shuffleInPlace(remaining, rng);

  const cornerA = remaining[0];
  const cornerB = remaining[1];
  if (cornerA !== undefined && cornerB !== undefined && suitOf(cornerA) === suitOf(cornerB)) {
    const want: SuitGroup = suitOf(cornerA) === "solids" ? "stripes" : "solids";
    for (let i = 2; i < remaining.length; i++) {
      const candidate = remaining[i];
      if (candidate !== undefined && suitOf(candidate) === want) {
        remaining[1] = candidate;
        remaining[i] = cornerB;
        break;
      }
    }
  }

  const ids: number[] = Array.from({ length: 15 }, () => 0);
  ids[0] = 1;
  ids[4] = 8;
  ids[10] = remaining[0] ?? 2;
  ids[14] = remaining[1] ?? 9;
  const used = new Set([1, 8, ids[10], ids[14]]);
  let slot = 0;
  for (let index = 0; index < 15; index++) {
    if (ids[index] !== 0) continue;
    while (slot < remaining.length && used.has(remaining[slot] ?? -1)) slot += 1;
    const id = remaining[slot] ?? 2;
    ids[index] = id;
    used.add(id);
    slot += 1;
  }

  const balls: PoolBall[] = [makePoolBall(0, POOL_HEAD_SPOT.x, POOL_HEAD_SPOT.y)];
  for (let index = 0; index < positions.length; index++) {
    const pos = positions[index];
    const id = ids[index];
    if (!pos || id === undefined) continue;
    balls.push(makePoolBall(id, pos.x, pos.y));
  }

  const world: PoolWorld = { balls, firstHitId: null };
  settlePoolWorld(world);
  return world;
}

export function countGroupOnTable(balls: readonly PoolBall[], group: SuitGroup): number {
  let n = 0;
  for (const ball of balls) {
    if (ball.pocketed) continue;
    if (suitOf(ball.id) === group) n += 1;
  }
  return n;
}

export function pocketedObjectIds(balls: readonly PoolBall[]): number[] {
  return balls
    .filter((ball) => ball.pocketed && ball.id !== 0)
    .map((ball) => ball.id)
    .sort((a, b) => a - b);
}

function playerName(seat: EightballSeat): string {
  return seat === 0 ? "Gracz 1" : "Gracz 2";
}

export function legalFirstGroup(
  openTable: boolean,
  shooterGroup: SuitGroup | null,
  remainingOwn: number,
): SuitGroup | "eight" | "any" {
  if (openTable || shooterGroup === null) return "any";
  if (remainingOwn <= 0) return "eight";
  return shooterGroup;
}

export function isLegalFirstHit(
  openTable: boolean,
  shooterGroup: SuitGroup | null,
  remainingOwn: number,
  firstHitId: number | null,
): boolean {
  if (firstHitId === null || firstHitId === 0) return false;
  const needed = legalFirstGroup(openTable, shooterGroup, remainingOwn);
  const hit = ballGroup(firstHitId);
  if (!hit) return false;
  if (needed === "any") return hit === "solids" || hit === "stripes" || hit === "eight";
  return hit === needed;
}

export function resolveEightballShot(input: EightballShotInput): EightballShotResult {
  const opponent = otherSeat(input.shooter);
  const shooterGroup = input.groups[input.shooter];
  const pocketedCue = input.pocketedIds.includes(0);
  const pocketedEight = input.pocketedIds.includes(8);
  const objectPocketed = input.pocketedIds.filter((id) => id !== 0 && id !== 8);

  const remainingOwn = shooterGroup ? countGroupOnTable(input.balls, shooterGroup) : 0;
  const remainingOwnBefore = shooterGroup
    ? remainingOwn + objectPocketed.filter((id) => suitOf(id) === shooterGroup).length
    : 0;

  let foul: EightballFoul | null = null;
  if (pocketedCue) foul = "scratch";
  else if (input.firstHitId === null) foul = "no_hit";
  else if (!isLegalFirstHit(input.openTable, shooterGroup, remainingOwnBefore, input.firstHitId)) {
    foul = "wrong_first";
  }

  let openTable = input.openTable;
  let groups: [SuitGroup | null, SuitGroup | null] = [input.groups[0], input.groups[1]];

  if (openTable && foul === null) {
    const firstObj = objectPocketed[0];
    const assigned = firstObj !== undefined ? suitOf(firstObj) : null;
    if (assigned) {
      groups[input.shooter] = assigned;
      groups[opponent] = assigned === "solids" ? "stripes" : "solids";
      openTable = false;
    }
  }

  if (pocketedEight) {
    const shooterSuit = groups[input.shooter];
    const ownNow = shooterSuit ? countGroupOnTable(input.balls, shooterSuit) : remainingOwn;
    const cleared = !input.openTable && shooterSuit !== null && ownNow === 0;
    const shooterWins = cleared && foul === null;
    const winner = shooterWins ? input.shooter : opponent;
    const message = shooterWins
      ? `${playerName(winner)} wbija ósemkę i wygrywa.`
      : pocketedCue
        ? `${playerName(input.shooter)} wbił ósemkę z faulem. ${playerName(winner)} wygrywa.`
        : `${playerName(input.shooter)} wbił ósemkę za wcześnie. ${playerName(winner)} wygrywa.`;
    return {
      foul,
      openTable,
      groups,
      keepTurn: false,
      ballInHand: false,
      gameEnded: true,
      winner,
      message,
    };
  }

  const ownPocketed =
    groups[input.shooter] !== null &&
    objectPocketed.some((id) => suitOf(id) === groups[input.shooter]);
  const keepTurn =
    foul === null &&
    (openTable ? objectPocketed.length > 0 : ownPocketed || (input.openTable && objectPocketed.length > 0));

  let message = "";
  if (foul) message = EIGHTBALL_FOUL_LABEL[foul];
  else if (!openTable && groups[0] && groups[1] && input.openTable) {
    message = `Grupy: Gracz 1 — ${EIGHTBALL_GROUP_LABEL[groups[0]]}, Gracz 2 — ${EIGHTBALL_GROUP_LABEL[groups[1]]}.`;
  }

  return {
    foul,
    openTable,
    groups,
    keepTurn,
    ballInHand: foul !== null,
    gameEnded: false,
    winner: null,
    message,
  };
}

export function groupAssignedMessage(
  groups: [SuitGroup | null, SuitGroup | null],
  names: readonly [string, string],
): string | null {
  const a = groups[0];
  const b = groups[1];
  if (!a || !b) return null;
  return `Grupy: ${names[0]} — ${EIGHTBALL_GROUP_LABEL[a]}, ${names[1]} — ${EIGHTBALL_GROUP_LABEL[b]}.`;
}

/**
 * Aim helpers for the cue line. Pure geometry, same 2R contact as the physics.
 */
export interface AimContact {
  cueSurf: PoolVec;
  hasHit: boolean;
  contact: PoolVec | null;
  objDir: PoolVec | null;
  cueTangent: PoolVec | null;
  normalMag: number;
  tangentMag: number;
}

export function aimContact(balls: readonly PoolBall[], angleDeg: number): AimContact | null {
  const cue = findPoolBall(balls, 0);
  if (!cue || cue.pocketed) return null;
  const a = (clampAngleDeg(angleDeg) * Math.PI) / 180;
  const dx = Math.cos(a);
  const dy = Math.sin(a);
  const r = POOL.BALL_R;
  const cueSurf = { x: cue.x + dx * r, y: cue.y + dy * r };

  let bestT = Infinity;
  let hit: PoolBall | null = null;
  for (const ball of balls) {
    if (ball.id === 0 || ball.pocketed) continue;
    const lx = ball.x - cue.x;
    const ly = ball.y - cue.y;
    const tca = lx * dx + ly * dy;
    if (tca <= 0) continue;
    const d2 = lx * lx + ly * ly - tca * tca;
    const rr = (2 * r) * (2 * r);
    if (d2 > rr) continue;
    const thc = Math.sqrt(rr - d2);
    const t = tca - thc;
    if (t > 0 && t < bestT) {
      bestT = t;
      hit = ball;
    }
  }

  if (!hit) {
    return {
      cueSurf,
      hasHit: false,
      contact: null,
      objDir: null,
      cueTangent: null,
      normalMag: 0,
      tangentMag: 0,
    };
  }

  const cueContact = { x: cue.x + dx * bestT, y: cue.y + dy * bestT };
  const nxRaw = hit.x - cueContact.x;
  const nyRaw = hit.y - cueContact.y;
  const nLen = Math.hypot(nxRaw, nyRaw) || 1;
  const nx = nxRaw / nLen;
  const ny = nyRaw / nLen;
  const contact = { x: cueContact.x + nx * r, y: cueContact.y + ny * r };
  let tx = -ny;
  let ty = nx;
  if (dx * tx + dy * ty < 0) {
    tx = -tx;
    ty = -ty;
  }
  const normalMag = Math.abs(dx * nx + dy * ny);
  const tangentMag = Math.sqrt(Math.max(0, 1 - normalMag * normalMag));
  return {
    cueSurf,
    hasHit: true,
    contact,
    objDir: { x: nx, y: ny },
    cueTangent: { x: tx, y: ty },
    normalMag,
    tangentMag,
  };
}

export function defaultSeatName(seat: EightballSeat): string {
  return seat === 0 ? "Gracz 1" : "Gracz 2";
}
